/**
 * Auth system types for workspaces, user profiles, and memberships.
 */

export type WorkspaceRole = 'owner' | 'admin' | 'editor' | 'viewer';
export type PlatformRole = 'user' | 'expert' | 'super_admin' | 'platform_owner';

/**
 * Which product a client bought. Describes the ACCOUNT, unlike the role types
 * above which describe the user. Drives which navigation surfaces render.
 */
export type ProductPackage = 'seo' | 'ads' | 'both';

/**
 * Resolve a package for gating. Fails OPEN to 'both' for anything unknown or
 * absent: hiding a surface the client paid for reads as the feature having
 * vanished, which is worse than showing one tab too many. Mirrors
 * normalize_product_package() on the backend.
 */
export function resolveProductPackage(
  value: string | null | undefined,
): ProductPackage {
  const normalized = (value ?? '').trim().toLowerCase();
  return normalized === 'seo' || normalized === 'ads' || normalized === 'both'
    ? normalized
    : 'both';
}

export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  display_name?: string;
  avatar_url?: string;
  platform_role: PlatformRole;
  /** Set only for platform_role='expert' — scopes their support-ticket view. */
  department?: 'technical' | 'seo' | 'web_dev' | 'ai' | 'ads' | 'communications' | null;
  is_active: boolean;
  /** The workspace the user most recently had active (#1262). Used to restore
   *  the right workspace on a fresh load instead of defaulting to ws[0]. */
  last_active_workspace_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  website?: string;
  is_active: boolean;
  /** Tenant has a valid Google Ads customer id — gates the ads-section picker. */
  has_google_ads?: boolean;
  /**
   * Which product the client bought. Distinct from `has_google_ads`, which is
   * inferred from whether a customer id happens to be set and says nothing
   * about whether they also bought SEO.
   *
   * Optional on the wire so a client on an older payload keeps every surface
   * it already had — read it through resolveProductPackage().
   */
  product_package?: ProductPackage;
  /**
   * Ads tier (DTC/B2B/Enterprise = 1/2/3), reused from google_ads_product_tier.
   * Optional on the wire; read it through resolveTier() in lib/entitlements.
   */
  google_ads_product_tier?: number;
  /**
   * Ads feature keys (F1..F9) this workspace is entitled to, computed by the
   * backend under ADS_ENTITLEMENTS_ENABLED. The runtime source of truth for
   * feature gating — see lib/entitlements. Absent on an older payload, which
   * the resolver treats as "all features" (ship dark) so nothing is hidden.
   */
  enabled_features?: string[];
  /**
   * tenant_settings.status — "active" | "paused" | "offboarded". Drives the
   * workspace switcher's "hide offboarded" filter. Optional so an older
   * payload defaults to "active" and nothing disappears.
   */
  tenant_status?: 'active' | 'paused' | 'offboarded';
  created_at?: string;
  updated_at?: string;
}

export interface WorkspaceMember {
  user_id: string;
  workspace_id: string;
  role: WorkspaceRole;
  joined_at?: string;
  email?: string | null;
  full_name?: string | null;
  display_name?: string | null;
  is_active?: boolean;
  avatar_url?: string | null;
  is_internal?: boolean;
}

export interface CreateWorkspaceRequest {
  name: string;
  website?: string;
}

export interface UpdateProfileRequest {
  display_name?: string;
  full_name?: string;
  avatar_url?: string;
}

export interface WorkspaceInvitation {
  id: string;
  email: string;
  role: WorkspaceRole;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expires_at?: string;
  created_at?: string;
}

export interface SendInvitationRequest {
  email: string;
  role: 'admin' | 'editor' | 'viewer';
}

export interface InvitationInfo {
  workspace_name: string;
  role: WorkspaceRole;
  invited_by_name: string;
  expires_at: string;
  status: string;
  is_valid: boolean;
  email: string;
}
